import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { Wind, Wifi, Tv, ShieldCheck, Waves, UtensilsCrossed, Bath, Shirt, Phone, Flame, BedDouble } from "lucide-react";
import { listRoomsAndBlocks } from "@/lib/booking.functions";
import { computeEffectivePrice, type Season } from "@/lib/pricing";
import { canonical } from "@/lib/seo";

export const Route = createFileRoute("/rooms")({
  head: () => ({
    meta: [
      { title: "Rooms - Malak BnB Resort" },
      { name: "description", content: "Explore our beautifully designed suites at Malak BnB Resort. Plush bedding, modern amenities, and tasteful décor near Crystal Beach." },
      { property: "og:title", content: "Rooms - Malak BnB Resort" },
      { property: "og:description", content: "Luxurious accommodations just steps from Crystal Beach and Lake Erie." },
      { property: "og:url", content: "https://malakbnbresort.ca/rooms" },
      { property: "og:image", content: "/images/room-comfort.webp" },
    ],
    links: [canonical("/rooms")],
  }),
  component: RoomsPage,
});

const ROOMS = [
  {
    name: "2 Double Beds",
    description: "This room includes 2 double beds with fine bedding, plenty of pillows, flat-screen TV with over 50 stations, a night table, a dresser, a phone and hardwood flooring.",
    size: "450 sq ft",
    beds: "2 Double(s)",
    price: "C$345",
    image: "/images/room-double-beds.webp",
    amenities: [Wind, Wifi, Tv, Phone, Bath, ShieldCheck],
    href: "/2-double-beds-room-crystal-beach",
  },
  {
    name: "Standard Double Bed",
    description: "This standard sized room includes a queen-sized bed with fine bedding, plenty of pillows, flat-screen TV with over 50 stations, a night table, a dresser, a phone and hardwood flooring.",
    size: "400 sq ft",
    beds: "1 Queen(s)",
    price: "C$345",
    image: "/images/room-standard-double.webp",
    amenities: [Wind, Wifi, Tv, Phone, Bath, ShieldCheck],
    href: null,
  },
  {
    name: "Deluxe Queen Bed",
    description: "This deluxe room includes a queen-sized bed with fine bedding, plenty of pillows, flat-screen TV with over 50 stations, a night table, a dresser, an electric fireplace, a phone and hardwood flooring.",
    size: "400 sq ft",
    beds: "1 Queen(s)",
    price: "C$355",
    image: "/images/room-deluxe-queen.webp",
    amenities: [Wind, Wifi, Tv, Flame, Phone, Bath],
    href: "/deluxe-queen-room-crystal-beach",
  },
  {
    name: "Queen Bed Suite",
    description: "This deluxe suite includes a queen-sized bed with fine bedding, plenty of pillows, flat-screen TV with over 50 stations, a night table, a dresser, a phone and spacious living area.",
    size: "440 sq ft",
    beds: "2 Queen(s)",
    price: "C$380",
    image: "/images/room-queen-suite.webp",
    amenities: [Wind, Wifi, Tv, Phone, Bath],
    href: "/queen-bed-suite-crystal-beach",
  },
];

const GALLERY = [
  "/images/gallery-private-bath.webp",
  "/images/gallery-bedroom-curtains.webp",
  "/images/gallery-pool-outdoor.webp",
  "/images/gallery-shower.webp",
];

const sharedAmenities = [
  { icon: Wind, label: "Air Conditioning" },
  { icon: Wifi, label: "Free Wi-Fi" },
  { icon: Tv, label: "Flat Screen TV" },
  { icon: ShieldCheck, label: "In-Room Safe" },
  { icon: Bath, label: "Private Bathroom" },
  { icon: Shirt, label: "Fresh Towels" },
  { icon: Waves, label: "Pool Access" },
  { icon: UtensilsCrossed, label: "Breakfast Included" },
];

function RoomsPage() {
  const listFn = useServerFn(listRoomsAndBlocks);
  const { data } = useQuery({ queryKey: ["rooms-and-blocks"], queryFn: () => listFn() });
  const today = new Date();
  const priceFor = (roomName: string, fallback: string): string => {
    const dbRoom = data?.rooms.find((r) => r.name === roomName);
    if (!dbRoom) return fallback;
    const effective = computeEffectivePrice(Number(dbRoom.price_cad), today, (data?.seasons ?? []) as Season[]);
    return `C$${effective}`;
  };
  return (
    <div>
      {/* Hero */}
      <section className="relative h-[50vh] min-h-[350px] overflow-hidden">
        <img
          src="/images/room-comfort.webp"
          alt="Malak BnB Resort room"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground/60 to-transparent" />
        <div className="relative z-10 flex h-full items-end pb-14 px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-7xl w-full">
            <h1 className="text-4xl sm:text-5xl font-heading text-white animate-fade-in-up">Our Rooms</h1>
            <p className="mt-3 text-lg text-white/80 max-w-xl">
              Spacious rooms with desk, phone, side table, night table, dresser &amp; more
            </p>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="py-20">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-heading text-foreground">Luxurious Accommodations Await</h2>
          <p className="mt-6 text-muted-foreground leading-relaxed">
            At Malak BnB Resort, our beautifully designed suites offer the perfect blend of comfort and elegance.
            Each room features plush bedding, modern amenities, and tasteful décor to create a serene sanctuary
            for your stay. Whether you're on a romantic getaway, family vacation, or solo retreat, our
            accommodations are designed to rejuvenate your spirit.
          </p>
          <p className="mt-4 text-muted-foreground leading-relaxed">
            Please explore our rooms below. When you're ready to book, simply use our{" "}
            <Link to="/book" className="text-primary font-semibold hover:underline">
              Request Reservation
            </Link>{" "}
            button so we can personally confirm your stay and share payment details with you directly.
          </p>
        </div>
      </section>

      {/* Individual Room Listings */}
      <section className="pb-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 space-y-10">
          {ROOMS.map((room) => (
            <div
              key={room.name}
              className="bg-card border border-border/50 rounded-2xl overflow-hidden flex flex-col md:flex-row"
            >
              {/* Room Image */}
              <div className="md:w-[340px] flex-shrink-0">
                <img
                  src={room.image}
                  alt={room.name}
                  className="w-full h-64 md:h-full object-cover"
                  loading="lazy"
                />
              </div>

              {/* Room Details */}
              <div className="flex-1 p-6 sm:p-8 flex flex-col justify-between">
                <div>
                  <h3 className="text-2xl font-heading text-foreground">{room.name}</h3>
                  <p className="mt-3 text-muted-foreground leading-relaxed">{room.description}</p>
                  <ul className="mt-4 space-y-1 text-sm text-foreground">
                    <li className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-primary" />
                      Size: {room.size}
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-primary" />
                      Beds: {room.beds}
                    </li>
                  </ul>

                  {/* Room amenity icons */}
                  <div className="mt-5 pt-5 border-t border-border/50 flex flex-wrap gap-3">
                    {room.amenities.map((Icon, i) => (
                      <div key={i} className="p-2 rounded-lg bg-background border border-border/30">
                        <Icon className="h-5 w-5 text-muted-foreground" />
                      </div>
                    ))}
                  </div>
                  {room.href && (
                    <Link to={room.href as any} className="mt-4 inline-flex items-center text-primary font-semibold text-sm hover:underline">
                      Room details →
                    </Link>
                  )}
                </div>
              </div>

              {/* Price */}
              <div className="md:w-[160px] flex-shrink-0 flex flex-col items-center justify-center p-6 border-t md:border-t-0 md:border-l border-border/50 bg-background">
                <span className="text-xs text-muted-foreground uppercase tracking-wider">From</span>
                <span className="text-3xl font-heading text-foreground mt-1">{priceFor(room.name, room.price)}</span>
                <span className="text-xs text-muted-foreground mt-1">per night</span>
                <Link
                  to="/book"
                  className="mt-4 inline-flex items-center justify-center rounded-full bg-primary text-primary-foreground px-5 py-2 text-xs font-semibold hover:bg-primary/90 transition-colors"
                >
                  Request
                </Link>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Shared Amenities */}
      <section className="py-16 bg-card">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h3 className="text-center text-2xl font-heading text-foreground mb-10">All Rooms Include</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
            {sharedAmenities.map((a) => (
              <div key={a.label} className="flex flex-col items-center gap-3 p-5 rounded-2xl bg-background border border-border/50">
                <a.icon className="h-7 w-7 text-primary" />
                <span className="text-sm font-medium text-foreground text-center">{a.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h3 className="text-center text-2xl font-heading text-foreground mb-10">Room Gallery</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {GALLERY.map((src, i) => (
              <div key={i} className="rounded-2xl overflow-hidden aspect-[4/3]">
                <img src={src} alt={`Room gallery ${i + 1}`} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" loading="lazy" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Related */}
      <section className="pb-4">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 text-center">
          <Link to="/seasonal-rates-malak-bnb-resort" className="text-primary font-semibold text-sm hover:underline">
            See seasonal rates and what's included →
          </Link>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-primary">
        <div className="mx-auto max-w-3xl px-4 text-center">
          <h2 className="text-3xl font-heading text-primary-foreground">Book Your Stay Today</h2>
          <p className="mt-4 text-primary-foreground/80">
            Submit a reservation request and we'll personally confirm your stay and share payment details with you directly.
          </p>
          <Link
            to="/book"
            className="mt-8 inline-flex items-center justify-center rounded-full bg-white text-primary px-10 py-4 text-sm font-semibold hover:bg-white/90 transition-colors shadow-lg"
          >
            Request Reservation
          </Link>
        </div>
      </section>
    </div>
  );
}
